import { CRORE } from '../../../../../../utils/utils';

export type MetricTrend = 'up' | 'down' | 'flat';

// A metric ready for display: raw API values transformed into formatted strings.
export interface DisplayMetric {
    key: string;
    label: string;
    value: string;
    delta: string | null;
    trend: MetricTrend | null;
    sparkline: { value: number }[];
    periodLabel: string;
}

// Trend stroke colours for the sparkline (recharts needs literal colour values,
// so these mirror the pill colours defined in the SCSS module). `flat` is the
// neutral grey used when there is no change (or only a negligible one).
export const KPI_TREND_COLORS: Record<MetricTrend, string> = {
    up: '#218c54',
    down: '#df666a',
    flat: '#9d9d9d',
};

// A change that rounds to 0.0% is treated as "no change" → neutral styling.
const FLAT_THRESHOLD = 0.05;

// Shown when a metric has no value; also used to detect an empty card.
export const METRIC_PLACEHOLDER = '—';

// API metricName → on-screen label. Order here is the render order.
// Big headline KPI cards (value + delta pill + trend graph).
export const KPI_METRIC_CONFIG: { metricName: string; label: string }[] = [
    { metricName: 'Revenue', label: 'Revenue' },
    { metricName: 'EBITDA Margin (%)', label: 'EBITDA Margin %' },
    { metricName: 'Profit After Tax', label: 'Profit (PAT)' },
    { metricName: 'Cash And Cash Equivalent', label: 'Cash' },
];

// Smaller cards handled in a later step.
export const SPARKLINE_METRIC_CONFIG: { metricName: string; label: string }[] = [
    { metricName: 'Revenue Growth (%)', label: 'Revenue Growth' },
    { metricName: 'Current Liabilities', label: 'Current Liabilities' },
    { metricName: 'Net Working Capital', label: 'Net Working Capital' },
    { metricName: 'Long Term Debt', label: 'Long Term Debt' },
    { metricName: 'Short Term Debt', label: 'Short Term Debt' },
    { metricName: 'Tangible Net Worth', label: 'Tangible Net Worth' },
];

// Currency in ₹ Cr, percentages as a rounded whole number with a % suffix.
export function formatMetricValue(value: number | null | undefined, unit: string): string {
    if (value === null || value === undefined) return METRIC_PLACEHOLDER;
    if (unit === '%') return `${Math.round(value)}%`;
    return `₹${(value / CRORE).toFixed(2)} Cr`;
}

// Year-over-year change of the latest FY vs the prior FY. Negligible movement is
// reported as a neutral `flat` trend so the UI shows grey instead of green/red.
export function computeDelta(current: number | null | undefined, previous: number | null | undefined) {
    if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) {
        return { delta: null as string | null, trend: null as MetricTrend | null };
    }
    const change = ((current - previous) / Math.abs(previous)) * 100;
    const trend: MetricTrend = Math.abs(change) < FLAT_THRESHOLD ? 'flat' : change > 0 ? 'up' : 'down';
    const sign = change > 0 ? '+' : '';
    const delta = `${sign}${change.toFixed(1)}%`;
    return { delta, trend };
}
